import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, LogOut, Eye, EyeOff, Trash2 } from "lucide-react";
import FlowerButton from "@/components/FlowerButton";
import { Stars } from "@/components/Reviews";
import { findProduct } from "@/data/mellow";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const date = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) : "—");

export default function AdminReviews() {
  const [secret, setSecret] = useState(() => sessionStorage.getItem("mm_admin_secret") || "");
  const [input, setInput] = useState("");
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (key) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/reviews`, { headers: { "X-Admin-Secret": key } });
      if (res.status === 401) { sessionStorage.removeItem("mm_admin_secret"); setSecret(""); toast.error("Mot de passe incorrect"); return; }
      setList((await res.json()).reviews);
      sessionStorage.setItem("mm_admin_secret", key); setSecret(key);
    } catch { toast.error("Impossible de charger les avis"); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (secret) load(secret); }, []); // eslint-disable-line

  const toggle = async (r) => {
    const res = await fetch(`${API}/admin/reviews/${r.id}/toggle`, { method: "POST", headers: { "X-Admin-Secret": secret } });
    if (!res.ok) return toast.error("Action impossible");
    const { hidden } = await res.json();
    setList((l) => l.map((x) => (x.id === r.id ? { ...x, hidden } : x)));
    toast.success(hidden ? "Avis masqué" : "Avis publié ✿");
  };
  const del = async (r) => {
    if (!window.confirm(`Supprimer définitivement l'avis de ${r.name} ?`)) return;
    const res = await fetch(`${API}/admin/reviews/${r.id}`, { method: "DELETE", headers: { "X-Admin-Secret": secret } });
    if (!res.ok) return toast.error("Suppression impossible");
    setList((l) => l.filter((x) => x.id !== r.id));
    toast.success("Avis supprimé");
  };

  if (!secret || list === null) {
    return (
      <div className="max-w-md mx-auto px-6 py-24 text-center" data-testid="admin-reviews-login">
        <Lock className="w-8 h-8 mx-auto text-[#78353A]" />
        <h1 className="font-cursive text-5xl text-[#78353A] mt-3">Les mots doux</h1>
        <form onSubmit={(e) => { e.preventDefault(); load(input); }} className="mt-6 flex flex-col items-center gap-4">
          <input type="password" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Mot de passe" data-testid="admin-reviews-secret-input"
            className="w-full rounded-full border-2 border-[#D1D5CB] bg-[#FFFDF9] px-5 py-3 text-[#4A3B32]" />
          <FlowerButton type="submit" data-testid="admin-reviews-login-btn">{loading ? "…" : "Entrer"}</FlowerButton>
        </form>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-14" data-testid="admin-reviews">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="font-cursive text-2xl text-[#78353A]/70">Espace privé</p>
          <h1 className="font-cursive text-6xl text-[#78353A] leading-none">Les mots doux</h1>
          <p className="mt-2 text-sm text-[#4A3B32]/70"><span data-testid="reviews-count">{list.length}</span> avis · <Link to="/admin/commandes" className="underline decoration-dotted">Commandes →</Link> · <Link to="/admin/newsletter" className="underline decoration-dotted">Newsletter →</Link></p>
        </div>
        <button onClick={() => { sessionStorage.removeItem("mm_admin_secret"); setSecret(""); setList(null); }} data-testid="reviews-logout" className="inline-flex items-center gap-2 rounded-full px-4 py-2 font-cursive text-xl text-[#78353A]/70"><LogOut className="w-4 h-4" /></button>
      </div>
      <div className="space-y-4">
        {list.length === 0 && <p className="text-center font-cursive text-3xl text-[#78353A]/60 py-16">Aucun avis pour le moment ✿</p>}
        {list.map((r) => (
          <div key={r.id} className={`rounded-3xl border-2 border-dashed border-[#D1D5CB] bg-[#FAF9F6] p-5 ${r.hidden ? "opacity-60" : ""}`} data-testid={`admin-review-${r.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <Stars value={r.rating} />
                <span className="font-cursive text-2xl text-[#78353A]">{r.name}</span>
                <span className="text-xs text-[#4A3B32]/60">{date(r.created_at)} · {findProduct(r.product_key)?.name || r.product_key}</span>
                {r.hidden && <span className="text-xs px-2 py-0.5 rounded-full bg-[#FADADD] text-[#78353A]">Masqué</span>}
              </div>
              <div className="flex gap-2">
                <button onClick={() => toggle(r)} data-testid={`review-toggle-${r.id}`} className="inline-flex items-center gap-2 rounded-full border-2 border-dashed border-[#C8D0B7] px-3 py-1.5 text-sm text-[#78353A]">{r.hidden ? <><Eye className="w-4 h-4" /> Publier</> : <><EyeOff className="w-4 h-4" /> Masquer</>}</button>
                <button onClick={() => del(r)} data-testid={`review-delete-${r.id}`} className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm text-[#78353A] hover:bg-[#FADADD]"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            <p className="mt-3 text-sm text-[#4A3B32]/90">« {r.message} »</p>
          </div>
        ))}
      </div>
    </div>
  );
}
